<template>
    <transition name="fade">
        <div v-if="isVisible" class="fixed inset-0 z-[9999] bg-[#3B3C46] flex flex-col justify-center items-center px-4 text-white font-body">
            <img :src="resolveAsset('/assets-site/images/age-gate/bg.svg')" alt="bgImage" class="absolute inset-0 w-full h-[90vh] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 object-contain object-center pointer-events-none" />
            <div class="w-full flex flex-col items-center space-y-8 text-center">
                <img :src="resolveAsset('/assets-site/images/header/logo.svg')" alt="Logo" class="h-24 w-auto object-contain mb-[60px]" />
                
                <p class="font-['alternate-gothic-compressed'] font-extrabold uppercase text-[40px] tracking-[1%] leading-[116%] mb-[40px] text-[#F3F3F3]">
                    You must be at least 18 years of age to view this site
                </p>

                <div class="max-w-[500px] z-10 flex flex-col gap-4 w-full mb-[60px]">
                    <div class="flex w-full flex-row gap-[25px] font-['alternate-gothic-compressed'] text-[32px]">
                        <input type="tel" inputmode="numeric" pattern="[0-9]*" v-model="month" placeholder="MM" class="w-1/3 bg-transparent border border-[#736B56] border-[4pt] text-[17px] bg-white text-center py-2 text-black outline-none transition font-body" min="1" max="12" @blur="formatMonth" @keyup.enter="handleAgeVerified()" />
                        <input type="tel" inputmode="numeric" pattern="[0-9]*" v-model="day" placeholder="DD" class="w-1/3 bg-transparent border border-[#736B56] border-[4pt] text-[17px] bg-white text-center py-2 text-black outline-none transition font-body" min="1" max="31" @blur="formatDay" @keyup.enter="handleAgeVerified()" />
                        <input type="tel" inputmode="numeric" pattern="[0-9]*" v-model="year" placeholder="YYYY" class="w-1/3 bg-transparent border border-[#736B56] border-[4pt] text-[17px] bg-white text-center py-2 text-black outline-none transition font-body" min="1900" @keyup.enter="handleAgeVerified()" />
                    </div>
                    <div v-if="error" class="text-[#F3F3F3] font-bold text-md mt-[10px]">{{ error }}</div>
                </div>
                <button @click="handleAgeVerified()" 
                        @mouseenter="onHover" 
                        @mouseleave="onHoverLeave"
                        class="relative overflow-hidden py-[15px] font-thin px-[30px] text-white font-body text-[14px] tracking-[23%] uppercase leading-none flex items-center justify-center max-w-[200px] mx-auto cursor-pointer">
                    
                    <!-- Skewed Background Wrapper -->
                    <div class="absolute -inset-y-2 -left-[50px] -right-[50px] overflow-hidden bg-[#181818] z-0 pointer-events-none select-none" style="transform: skewX(-25deg);">
                        <!-- Sand Layer -->
                        <div ref="sandBg" class="absolute inset-y-0 bg-[#A29375] w-full" style="left: -45%;"></div>
                        <!-- Entering Black Layer -->
                        <div ref="blackBg" class="absolute inset-y-0 bg-[#181818] w-full" style="left: -100%;"></div>
                    </div>
                    
                    <!-- Text / Content -->
                    <span class="relative z-10 pl-[.4em]">Submit</span>
                </button>
            </div>
        </div>
    </transition>
</template>

<script>
import { gsap } from "gsap";
import { resolveAsset } from '@/components/helpers/asset';

export default {
    name: 'AgeGate',

    data() {
        return {
            isVisible: false,
            day: '',
            month: '',
            year: '',
            error: ''
        };
    },
    mounted() {
        this.checkCookie();
    },
    beforeUnmount() {
        if (this.hoverTL) {
            this.hoverTL.kill();
        }
    },
    methods: {
        resolveAsset,
        initHoverAnimation() {
            if (this.hoverTL) return;
            if (!this.$refs.sandBg || !this.$refs.blackBg) return;

            this.hoverTL = gsap.timeline({ paused: true });
            
            this.hoverTL.to(this.$refs.sandBg, {
                xPercent: 45,
                duration: 0.5,
                ease: 'power2.inOut'
            })
            .to(this.$refs.blackBg, {
                xPercent: 100,
                duration: 0.5,
                ease: 'power2.inOut'
            }, '-=0.2');
        },
        onHover() {
            this.initHoverAnimation();
            if (this.hoverTL) {
                this.hoverTL.play();
            }
        },
        onHoverLeave() {
            if (this.hoverTL) {
                this.hoverTL.reverse();
            }
        },

        checkCookie() {
            let isVerified = false;
            try {
                const match = document.cookie.match(new RegExp('(^| )age_verified=([^;]+)'));
                if (match && match[2] === 'true') {
                    isVerified = true;
                }
            } catch (e) {
                console.error("Cookie read blocked:", e);
            }

            try {
                if (!isVerified && window.localStorage && window.localStorage.getItem('age_verified') === 'true') {
                    isVerified = true;
                }
            } catch (e) {
                console.error("Local storage read blocked:", e);
            }

            if (isVerified) {
                this.$emit('verified');
            } else {
                this.isVisible = true;
                this.$emit('gate-active');
            }
        },
        handleAgeVerified() {
            this.error = '';
            
            // Format month and day to ensure single digits are padded on submit
            this.formatMonth();
            this.formatDay();

            if (!this.day || !this.month || !this.year) {
                this.error = 'Please enter your date of birth.';
                return;
            }

            if (this.month.length !== 2 || this.day.length !== 2 || this.year.length !== 4) {
                this.error = 'Please use MM / DD / YYYY format.';
                return;
            }

            const d = parseInt(this.day, 10);
            const m = parseInt(this.month, 10);
            const y = parseInt(this.year, 10);

            const birthDate = new Date(y, m - 1, d);
            const today = new Date();

            // Simple and robust age calculation
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }

            if (age >= 18) {
                // Set cookie for 30 days
                const expiryDate = new Date();
                expiryDate.setTime(expiryDate.getTime() + (30 * 24 * 60 * 60 * 1000));
                const expires = "expires=" + expiryDate.toUTCString();
                try {
                    document.cookie = "age_verified=true;" + expires + ";path=/";
                } catch (e) {
                    console.error("Failed to write age cookie:", e);
                }

                try {
                    if (window.localStorage) {
                        window.localStorage.setItem('age_verified', 'true');
                    }
                } catch (e) {
                    console.error("Failed to write to local storage:", e);
                }
                
                this.isVisible = false;
                this.$emit('verified');
            } else {
                window.location.href = "https://www.google.com";
            }
        },
        formatDay() {
            if (!this.day) return;
            let num = parseInt(this.day, 10);
            if (isNaN(num) || num < 1) num = 1;
            if (num > 31) num = 31;
            this.day = num.toString().padStart(2, '0');
        },
        formatMonth() {
            if (!this.month) return;
            let num = parseInt(this.month, 10);
            if (isNaN(num) || num < 1) num = 1;
            if (num > 12) num = 12;
            this.month = num.toString().padStart(2, '0');
        }
    },
    watch: {
        isVisible(val) {
            if (val) {
                this.$nextTick(() => {
                    this.initHoverAnimation();
                });
            }
        },
        day(val) {
            let clean = val.replace(/\D/g, '').substring(0, 2);
            if (parseInt(clean, 10) > 31) clean = '31';
            this.day = clean;
        },
        month(val) {
            let clean = val.replace(/\D/g, '').substring(0, 2);
            if (parseInt(clean, 10) > 12) clean = '12';
            this.month = clean;
        },
        year(val) {
            this.year = val.replace(/\D/g, '').substring(0, 4);
        }
    }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
